/**
 * Kitchen Alert Service
 *
 * Wrapper for the native KitchenAlert plugin that manages background audio alerts.
 * Uses Android Foreground Service for:
 * - Background API polling every 30 seconds
 * - Continuous audio playback even when app is closed
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

// Register the native plugin
const KitchenAlert = registerPlugin('KitchenAlert');

class KitchenAlertService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.isPolling = false;
  }

  /**
   * Start background polling for pending orders
   * This will poll the API every 30 seconds even when app is in background
   * @param {string} authToken - Auth token for API calls
   * @param {string} userRole - User role for API endpoint selection
   */
  async startPolling(authToken, userRole = 'restaurant') {
    if (!this.isNative) {
      console.log('KitchenAlertService: startPolling only works on native');
      return false;
    }

    console.log('KitchenAlertService: Starting background polling, role=' + userRole);

    try {
      await KitchenAlert.startPolling({ authToken, userRole });
      this.isPolling = true;
      return true;
    } catch (error) {
      console.error('KitchenAlertService: Error starting polling', error);
      return false;
    }
  }

  /**
   * Stop background polling
   */
  async stopPolling() {
    if (!this.isNative) return false;

    console.log('KitchenAlertService: Stopping background polling');

    try {
      await KitchenAlert.stopPolling();
      this.isPolling = false;
      return true;
    } catch (error) {
      console.error('KitchenAlertService: Error stopping polling', error);
      return false;
    }
  }

  /**
   * Start alert for a new order (manual trigger)
   * @param {string|number} orderId - Unique order identifier
   * @param {string} orderNumber - Display order number
   */
  async startAlert(orderId, orderNumber = 'New Order') {
    if (!this.isNative) return false;

    const orderIdStr = String(orderId);
    console.log(`KitchenAlertService: Starting alert for order ${orderIdStr}`);

    try {
      await KitchenAlert.startAlert({ orderId: orderIdStr, orderNumber });
      return true;
    } catch (error) {
      console.error('KitchenAlertService: Error starting alert', error);
      return false;
    }
  }

  /**
   * Stop alert for a specific order
   * @param {string|number} orderId - Order to stop alert for
   */
  async stopAlert(orderId) {
    if (!this.isNative) return false;

    const orderIdStr = String(orderId);
    console.log(`KitchenAlertService: Stopping alert for order ${orderIdStr}`);

    try {
      await KitchenAlert.stopAlert({ orderId: orderIdStr });
      return true;
    } catch (error) {
      console.error('KitchenAlertService: Error stopping alert', error);
      return false;
    }
  }

  /**
   * Stop all active alerts and polling
   */
  async stopAllAlerts() {
    if (!this.isNative) return false;

    console.log('KitchenAlertService: Stopping all alerts');

    try {
      await KitchenAlert.stopAllAlerts();
      this.isPolling = false;
      return true;
    } catch (error) {
      console.error('KitchenAlertService: Error stopping all alerts', error);
      return false;
    }
  }

  /**
   * Check if alert is playing
   */
  async isAlertPlaying() {
    if (!this.isNative) return false;

    try {
      const result = await KitchenAlert.isAlertPlaying();
      return result.isPlaying;
    } catch (error) {
      console.error('KitchenAlertService: Error checking alert status', error);
      return false;
    }
  }

  /**
   * Check if polling is active
   */
  async isPollingActive() {
    if (!this.isNative) return false;

    try {
      const result = await KitchenAlert.isPollingActive();
      return result.isPolling;
    } catch (error) {
      console.error('KitchenAlertService: Error checking polling status', error);
      return false;
    }
  }
}

// Export singleton
const kitchenAlertService = new KitchenAlertService();
export default kitchenAlertService;
